/**
 * E2E Crypto Service V2 — libsodium sealed boxes + XChaCha20-Poly1305.
 *
 * Replaces V1 (tweetnacl X25519 + WebCrypto AES-256-GCM + HKDF).
 * Uses libsodium-wrappers for all crypto operations.
 */
import _sodium from "libsodium-wrappers"

let sodium = null

/**
 * Ensure libsodium WASM is initialized.
 * Must be called once before any crypto operation.
 */
export async function ensureSodiumReady() {
  if (sodium) return
  await _sodium.ready
  sodium = _sodium
}

/**
 * Generate a new X25519 key pair.
 * @returns {{ publicKey: string, privateKey: string }} base64-encoded keys
 */
export function generateKeyPair() {
  const kp = sodium.crypto_box_keypair()
  return {
    publicKey: sodium.to_base64(kp.publicKey, sodium.base64_variants.ORIGINAL),
    privateKey: sodium.to_base64(kp.privateKey, sodium.base64_variants.ORIGINAL),
  }
}

/**
 * Generate a random 256-bit symmetric key for a conversation.
 * @returns {Uint8Array} 32-byte key
 */
export function generateConversationKey() {
  return sodium.crypto_aead_xchacha20poly1305_ietf_keygen()
}

/**
 * Seal a conversation key for a recipient using crypto_box_seal.
 *
 * @param {Uint8Array} convKey             - 32-byte conversation key
 * @param {string}     recipientPubKeyB64  - base64 X25519 public key
 * @returns {string}                        - base64 sealed blob
 */
export function encryptConversationKey(convKey, recipientPubKeyB64) {
  const recipientPubKey = sodium.from_base64(recipientPubKeyB64, sodium.base64_variants.ORIGINAL)
  const sealed = sodium.crypto_box_seal(convKey, recipientPubKey)
  return sodium.to_base64(sealed, sodium.base64_variants.ORIGINAL)
}

/**
 * Open a sealed conversation key using our key pair.
 *
 * @param {string} sealedB64   - base64 sealed blob
 * @param {string} pubKeyB64   - base64 our public key
 * @param {string} privKeyB64  - base64 our private key
 * @returns {Uint8Array}        - 32-byte conversation key
 */
export function decryptConversationKeyV2(sealedB64, pubKeyB64, privKeyB64) {
  const sealed = sodium.from_base64(sealedB64, sodium.base64_variants.ORIGINAL)
  const pubKey = sodium.from_base64(pubKeyB64, sodium.base64_variants.ORIGINAL)
  const privKey = sodium.from_base64(privKeyB64, sodium.base64_variants.ORIGINAL)
  return sodium.crypto_box_seal_open(sealed, pubKey, privKey)
}

/**
 * Encrypt a message with XChaCha20-Poly1305.
 *
 * @param {string}    plaintext  - UTF-8 message text
 * @param {Uint8Array} convKey   - 32-byte conversation key
 * @returns {{ ciphertext: string, nonce: string }} base64-encoded
 */
export function encryptMessageV2(plaintext, convKey) {
  const nonce = sodium.randombytes_buf(sodium.crypto_aead_xchacha20poly1305_ietf_NPUBBYTES)
  const plaintextBytes = sodium.from_string(plaintext)
  const ciphertext = sodium.crypto_aead_xchacha20poly1305_ietf_encrypt(
    plaintextBytes, null, null, nonce, convKey,
  )
  return {
    ciphertext: sodium.to_base64(ciphertext, sodium.base64_variants.ORIGINAL),
    nonce: sodium.to_base64(nonce, sodium.base64_variants.ORIGINAL),
  }
}

/**
 * Decrypt a V2 message (XChaCha20-Poly1305).
 *
 * @param {string}    ciphertextB64  - base64 ciphertext (includes auth tag)
 * @param {string}    nonceB64       - base64 nonce
 * @param {Uint8Array} convKey       - 32-byte conversation key
 * @returns {string}                  - UTF-8 plaintext
 */
export function decryptMessageV2(ciphertextB64, nonceB64, convKey) {
  const ciphertext = sodium.from_base64(ciphertextB64, sodium.base64_variants.ORIGINAL)
  const nonce = sodium.from_base64(nonceB64, sodium.base64_variants.ORIGINAL)
  const plaintext = sodium.crypto_aead_xchacha20poly1305_ietf_decrypt(
    null, ciphertext, null, nonce, convKey,
  )
  return sodium.to_string(plaintext)
}
